import { Button } from '@components/ui'
import { useQuery } from '@tanstack/react-query'
import { handlePrint, removeSearchParams } from '@utils/assessmentReportUtils'
import { Flex, Result, Spin } from 'antd'
import { lazy, useEffect, useMemo, useState } from 'react'

import { Entities_FluencyGamesResult_PlayerResult } from '@suntex.fim/api-client'
import {
  gradeToGymLevel,
  gradeToJtfMode,
  gradeToKnowAndShowLevel,
  gradeToPhase
} from '@suntex.fim/assessment'
import { FluencyGamesReportView } from '@suntex.fim/assessment/lazy'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import {
  $GymMode,
  $JtfMode,
  $KnowAndShowLevel,
  $SpecialGradeLevel,
  EntityLevel,
  GoalsIndexPhase,
  GymMode,
  JtfMode,
  KnowAndShowLevel,
  SpecialGradeLevel
} from '@suntex.fim/enums'
import { getFlags } from '@suntex.fim/flags'
import { useFramework, useRotateDevice } from '@suntex.fim/framework'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalPlayerContext } from '@suntex.fim/player'
import { useOptionalTeamInfo } from '@suntex.fim/team'

import { resolveFluencyGamesEntity } from './AssessmentReportFluencyGames.helpers'

const AssessmentReportNavigation = lazy(
  () => import('./AssessmentReport/AssessmentReportNavigation')
)
const SelectWithReact = lazy(() => import('./AssessmentReport/SelectWithReact'))

/**
 * returns JSX for report summary
 */
const ALL = 'All'

function AssessmentReportFluencyGames() {
  const { api } = useFramework()
  const {
    gymLevel,
    kasLevel,
    jtfLevelOptions: queryJtfLevelOptions,
    playerId
  } = useAppQueryString()
  const { login } = useAuth(isAuthenticated)
  const team = useOptionalTeamInfo()
  const playerContext = useOptionalPlayerContext()
  const playerTeam = playerContext?.playerInfo?.team
  const isPlayerRole =
    login.entityLevel === EntityLevel.Player ||
    location.pathname.includes('player')
  const [selectedUserOptions, setSelectedUserOptions] = useState<
    {
      key: string
      label: string
    }[]
  >([
    {
      key: ALL,
      label: ALL
    }
  ])
  const [isDisplayJTF, setIsDisplayJTF] = useState(true)
  const [selectedPlayerFluencyGame, setSelectedPlayerFluencyGame] =
    useState<Entities_FluencyGamesResult_PlayerResult | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  useRotateDevice()
  const selectedUser = {
    level: String(playerId ?? ''),
    gymlevel: $GymMode.from(gymLevel ?? GymMode.WholeNumbers),
    kaslevel: $KnowAndShowLevel.from(
      kasLevel ??
        (team?.grade && team.grade >= 1 && team.grade <= 8
          ? team.grade
          : KnowAndShowLevel.KS1)
    ),
    jtfMode: $JtfMode.from(queryJtfLevelOptions || JtfMode.WholeNumbers)
  }
  const [filters, setFilters] = useState(selectedUser)

  const teamId = team?.id || playerTeam?.teamId
  const teamYear = team?.year || playerTeam?.year || ''
  const teamGrade =
    $SpecialGradeLevel.fromOptional(team?.grade) ??
    $SpecialGradeLevel.fromOptional(playerTeam?.grade) ??
    SpecialGradeLevel.Null

  // For indie players without a team, use the player's own year/grade
  const queryYear = teamYear || login.year
  const queryGrade =
    teamGrade !== SpecialGradeLevel.Null
      ? teamGrade
      : ($SpecialGradeLevel.fromOptional(
          playerContext?.playerInfo?.player?.grade
        ) ??
        $SpecialGradeLevel.fromOptional(login.gradeId) ??
        SpecialGradeLevel.Null)

  // Indie players (no team) aren't fully supported by the backend yet, so
  // show a "Coming Soon" message instead. Gated by a flag to flip on later.
  const isIndiePlayer = isPlayerRole && teamId == null
  const showIndieComingSoon =
    isIndiePlayer && !getFlags().player.indieProgressReport

  // A player viewing their own progress must be fetched at Player level: the
  // team-level report excludes family/teacher accounts, so Family Link players
  // would otherwise be missing from the returned data and the report would
  // not load. The team/admin report stays at Team level for the full roster.
  const {
    id: fluencyEntityId,
    level: fluencyLevel,
    enabled: fluencyEnabled
  } = resolveFluencyGamesEntity(isPlayerRole, playerContext?.playerId, teamId)
  const { data: fluencyResponse, isLoading } = useQuery({
    queryKey: [
      'assessment',
      'fluencyGames',
      fluencyLevel,
      fluencyEntityId,
      queryYear,
      queryGrade
    ],
    queryFn: () =>
      api.get_Assessment_FluencyGames({
        id: fluencyEntityId,
        year: queryYear,
        grade: queryGrade,
        level: fluencyLevel
      }),
    enabled: fluencyEnabled && !showIndieComingSoon
  })

  const teamFluencyGame = fluencyResponse?.fluencyGames?.[0]?.team || null
  const playerFluencyGame = useMemo(
    () =>
      [...(teamFluencyGame?.players ?? [])].sort((a, b) => {
        const userIdA = a.login.userId || ''
        const userIdB = b.login.userId || ''
        return userIdA.localeCompare(userIdB)
      }),
    [teamFluencyGame]
  )
  const stickerTeamAverag = teamFluencyGame?.stickerAverage ?? 0

  // Update user options and filters from response
  useEffect(() => {
    if (!fluencyResponse?.fluencyGames?.[0]) return
    const teamData = fluencyResponse.fluencyGames[0].team

    if (!isPlayerRole && teamData) {
      const userOptions = teamData.players?.map(
        (player: Entities_FluencyGamesResult_PlayerResult) => ({
          key: player.login.userId ? player.login.userId : '',
          label: player.login.userId
            ? `${player.login.userId} | ${player.login.displayName || 'NA'}`
            : ''
        })
      )
      const sortedUserOptions = userOptions?.sort((a, b) =>
        a.label.localeCompare(b.label)
      )
      const uniqueUserOptions = Array.from(
        new Map(sortedUserOptions?.map((item) => [item.key, item])).values()
      )

      if (playerId) {
        setFilters((filters) => ({ ...filters, level: String(playerId) }))
      } else {
        setFilters((filters) => ({
          ...filters,
          level: uniqueUserOptions[0]?.key ?? ''
        }))
      }

      const finalUserOptions = [
        { key: ALL, label: ALL },
        ...uniqueUserOptions.filter((option) => option.key !== ALL)
      ]
      setSelectedUserOptions(finalUserOptions || [])
    }

    // Indie players have no team, so default off their own grade instead.
    const reportGrade = teamData
      ? teamData.data.grade
      : fluencyResponse.fluencyGames[0].individualPlayer.data.grade
    // Kindergarten is grade 0, so guard on null rather than truthiness or the
    // gym level never defaults and lands on a mode the dropdown no longer offers.
    if (reportGrade != null && gymLevel === null) {
      const gymLevelBasedOnGrade = gradeToGymLevel(
        $SpecialGradeLevel.from(reportGrade)
      )
      setFilters((prevFilters) => ({
        ...prevFilters,
        gymlevel: gymLevelBasedOnGrade
      }))
    }
    if (
      reportGrade != null &&
      reportGrade >= SpecialGradeLevel.Grade3 &&
      !queryJtfLevelOptions
    ) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        jtfMode: gradeToJtfMode($SpecialGradeLevel.from(reportGrade))
      }))
    }
    if (reportGrade != null && kasLevel === null) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        // Indie players always start at Know & Show 1, regardless of grade,
        // since they have no team to base the level on the way a team does.
        kaslevel: teamData
          ? gradeToKnowAndShowLevel($SpecialGradeLevel.from(reportGrade))
          : KnowAndShowLevel.KS1
      }))
    }
  }, [fluencyResponse])
  useEffect(() => {
    if (isPlayerRole) {
      setFilters((prev) => ({ ...prev, level: login.userId || '' }))
    }
  }, [])

  useEffect(() => {
    const phase = gradeToPhase(
      $SpecialGradeLevel.from(
        team?.grade ??
          playerTeam?.grade ??
          playerContext?.playerInfo?.player?.grade ??
          SpecialGradeLevel.Other
      )
    )
    if (phase === GoalsIndexPhase.Early) {
      setIsDisplayJTF(false)
    }
  }, [team, playerTeam, playerContext])
  const memoizedPlayers = useMemo(() => playerFluencyGame, [playerFluencyGame])
  useEffect(() => {
    const fluencyGame = fluencyResponse?.fluencyGames?.[0]
    if (isPlayerRole && fluencyGame && fluencyGame.team === null) {
      const individualPlayer = fluencyGame.individualPlayer ?? null
      setSelectedPlayerFluencyGame(individualPlayer)
    } else if (memoizedPlayers) {
      const selectedPlayer = memoizedPlayers.find(
        (player) => player.login.userId === filters.level
      )
      setSelectedPlayerFluencyGame(selectedPlayer || null)
    }
  }, [filters.level, memoizedPlayers, isPlayerRole, fluencyResponse])

  return (
    <Flex vertical gap="20px" className="reportfluencygames">
      {showIndieComingSoon ? (
        <Result
          status="info"
          title="Coming Soon"
          subTitle="Progress reports for individual players are coming soon."
        />
      ) : isLoading || isExporting ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.7)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Spin size="large" tip="Loading..." />
        </div>
      ) : (
        <>
          <AssessmentReportNavigation
            exportButton={
              !isPlayerRole && (
                <div className="export-btn print-hidden">
                  <Button
                    shape="round"
                    intent="secondary"
                    onClick={() =>
                      handlePrint({
                        setIsLoading: setIsExporting,
                        fileName: `${team?.name}_fluency-games`
                      })
                    }
                  >
                    View Printer Friendly Version
                  </Button>
                </div>
              )
            }
            filters={
              !isPlayerRole && (
                <Flex gap={14}>
                  <Flex gap={6} flex="1 1 auto" vertical>
                    <p className="dd-label fw-bold text-sm">User ID</p>
                    <div className="select-dropdown-fluency">
                      <SelectWithReact
                        value={
                          String(filters.level) || selectedUserOptions[0]?.label
                        }
                        onChange={(level: string) => {
                          removeSearchParams('playerId')
                          setFilters((prev) => ({ ...prev, level }))
                        }}
                        options={selectedUserOptions}
                      />
                    </div>
                  </Flex>
                </Flex>
              )
            }
            displayReport={!isPlayerRole}
            isPlayer={isPlayerRole || playerId !== undefined}
            title="Fluency Games Report"
          />
          <FluencyGamesReportView
            teamData={teamFluencyGame}
            selectedPlayer={selectedPlayerFluencyGame}
            allPlayers={playerFluencyGame ?? []}
            filters={filters}
            onFiltersChange={setFilters}
            stickerTeamAverage={stickerTeamAverag}
            isDisplayJTF={isDisplayJTF}
            showAllPlayers={filters.level === ALL}
          />
        </>
      )}
    </Flex>
  )
}

export default AssessmentReportFluencyGames
